<template>
  <AppShell>
    <p v-if="error" class="error">
      {{ error }}
      <router-link class="linkish" to="/articles">返回行业资讯</router-link>
    </p>
    <p v-else-if="loading" class="muted">加载中…</p>
    <template v-else-if="article">
      <div class="toolbar">
        <router-link class="ghost" to="/articles">← 行业资讯</router-link>
        <button type="button" class="ghost" @click="copyLink">{{ copied ? '已复制链接' : '复制链接' }}</button>
      </div>

      <article class="panel">
        <h1>{{ article.title }}</h1>
        <p class="meta">
          <span v-if="article.published_at">{{ formatTime(article.published_at) }}</span>
          <router-link
            v-for="tag in tagList"
            :key="tag"
            class="tag"
            :to="{ path: '/articles', query: { tag } }"
          >{{ tag }}</router-link>
        </p>
        <p v-if="article.summary" class="summary">{{ article.summary }}</p>
        <div class="rich-body" v-html="article.body_html" />

        <section v-if="article.cards && article.cards.length" class="block">
          <h2>相关算力卡</h2>
          <div class="refs">
            <router-link
              v-for="card in article.cards"
              :key="card.id"
              class="ref"
              :to="{ path: '/', query: { q: card.name } }"
            >
              <strong>{{ card.name }}</strong>
              <span>查询参数</span>
            </router-link>
          </div>
        </section>

        <section v-if="article.related && article.related.length" class="block">
          <h2>相关资讯</h2>
          <ul class="related-list">
            <li v-for="item in article.related" :key="item.id">
              <router-link :to="`/articles/${encodeURIComponent(item.slug)}`">{{ item.title }}</router-link>
            </li>
          </ul>
        </section>
      </article>
    </template>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import { getArticle } from '../api/platformService'
import { clearPageMeta, setPageMeta } from '../utils/pageMeta'

export default {
  name: 'ArticleDetail',
  components: { AppShell },
  data() {
    return {
      article: null,
      loading: false,
      error: '',
      copied: false,
      copyTimer: null
    }
  },
  computed: {
    tagList() {
      return String(this.article?.tags || '')
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)
    }
  },
  watch: {
    '$route.params.slug': {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },
  beforeUnmount() {
    clearPageMeta()
    if (this.copyTimer) clearTimeout(this.copyTimer)
  },
  methods: {
    formatTime(iso) {
      if (!iso) return ''
      return String(iso).replace('T', ' ').slice(0, 16)
    },
    async copyLink() {
      const url = this.article?.canonical_url || window.location.href
      try {
        await navigator.clipboard.writeText(url)
      } catch (e) {
        window.prompt('复制链接', url)
      }
      this.copied = true
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copied = false
      }, 1600)
    },
    async load() {
      const slug = this.$route.params.slug
      if (!slug) return
      this.loading = true
      this.error = ''
      this.article = null
      this.copied = false
      try {
        const data = await getArticle(slug)
        this.article = data
        setPageMeta({
          title: `${data.title} · 算力百科`,
          description: data.summary || data.title,
          keywords: data.seo_keywords || data.tags,
          canonical: data.canonical_url || `${window.location.origin}/articles/${encodeURIComponent(data.slug)}`,
          jsonLd: data.json_ld || null,
          ogType: 'article',
          ogImage: data.og_image || `${window.location.origin}/icon.png`
        })
      } catch (err) {
        this.error = err.response?.data?.error || '文章不存在'
        clearPageMeta()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; align-items: center;
}
.ghost {
  display: inline-flex; align-items: center; border: 1px solid #e5e6eb; background: #fff;
  color: #1d2129; border-radius: 12px; padding: 6px 12px; font: inherit; font-size: 13px;
  cursor: pointer; text-decoration: none;
}
.ghost:hover { border-color: #165dff; color: #165dff; }
.panel {
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 22px 20px;
  max-width: 860px;
}
h1 {
  margin: 0 0 10px; font-size: 30px; font-weight: 650; letter-spacing: -0.03em; line-height: 1.3;
}
.meta {
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
  color: #86909c; font-size: 13px; margin: 0 0 14px;
}
.tag {
  display: inline-block; padding: 2px 8px; border-radius: 999px; background: #f2f3f5;
  color: #4e5969; text-decoration: none; font-size: 12px;
}
.tag:hover { background: #e8f3ff; color: #165dff; }
.summary { margin: 0 0 18px; color: #4e5969; line-height: 1.7; }
.rich-body { line-height: 1.85; color: #1d2129; }
.rich-body :deep(p) { margin: 0 0 12px; }
.rich-body :deep(h2), .rich-body :deep(h3) { margin: 20px 0 8px; letter-spacing: -0.02em; }
.rich-body :deep(ul), .rich-body :deep(ol) { margin: 0 0 12px 1.2em; }
.rich-body :deep(a) { color: #165dff; }
.rich-body :deep(img) { max-width: 100%; height: auto; border-radius: 8px; }
.rich-body :deep(table) { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 14px; }
.rich-body :deep(th), .rich-body :deep(td) { border: 1px solid #e5e6eb; padding: 6px 8px; }
.rich-body :deep(blockquote) {
  margin: 12px 0; padding: 8px 12px; border-left: 3px solid #bedaff; background: #f7f8fa; color: #4e5969;
}
.block { margin-top: 28px; padding-top: 18px; border-top: 1px solid #e5e6eb; }
.block h2 { margin: 0 0 10px; font-size: 16px; }
.refs { display: grid; gap: 8px; }
.ref {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  text-decoration: none; color: #1d2129; background: #f2f7ff; border: 1px solid #bedaff;
  border-radius: 12px; padding: 10px 12px;
}
.ref span { color: #165dff; font-size: 13px; white-space: nowrap; }
.ref:hover { border-color: #165dff; }
.related-list { list-style: none; margin: 0; padding: 0; }
.related-list li { padding: 8px 0; border-top: 1px solid #f2f3f5; }
.related-list li:first-child { border-top: 0; }
.related-list a { color: #165dff; text-decoration: none; }
.related-list a:hover { text-decoration: underline; }
.linkish { color: #165dff; margin-left: 8px; }
.error { color: #d3382c; }
.muted { color: #86909c; }
</style>
